import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
// eslint-disable-next-line no-restricted-imports
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { Centered } from 'atoms/layout/Centered';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LinkBox, LinkOther, LinkOverlay } from 'atoms/navigation/Box';
import { BodyS, BrandM, BrandS } from 'atoms/typography';
import { Img } from 'atoms/ui/Img';
import { CREDIT_TERMS } from 'constants/__generated__/routes';
import { useConnectionContext } from 'contexts/connection';
import { useSportContext } from 'contexts/sport';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useQuery } from 'hooks/graphql/useQuery';
import { useAmountWithConversion } from 'hooks/useAmountWithConversion';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { STORAGE, useLocalStorage } from 'hooks/useLocalStorage';
import { useEvents } from 'lib/events/useEvents';
import { monetaryAmountFragment } from 'lib/monetaryAmount';
import { laptopAndAbove } from 'style/mediaQuery';
import { borderGradientStyle } from 'style/utils';

import type {
  LandingPromoBannerQuery,
  LandingPromoBannerQueryVariables,
} from './__generated__/index.graphql';
import icon from './assets/Icon.png';
import bg from './assets/bg.jpg';
import mobileBg from './assets/mobileBg.jpg';

const LinkBoxWrapper = styled(LinkBox)`
  --border-gradient: var(--c-gradient-discount);
  --border-size: 2px;
  ${borderGradientStyle};
  background-image: url(${mobileBg});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 20px;
  padding: var(--unit) var(--double-unit);
  @media ${laptopAndAbove} {
    background-image: url(${bg});
  }
`;

const Content = styled.div`
  justify-content: flex-start;
  width: 100%;
`;

const Description = styled(BrandM)`
  @media ${laptopAndAbove} {
    font: var(--t-brand-m);
  }
`;

const UnderlinedText = styled.span`
  text-decoration: underline;
  &:hover {
    color: var(--c-nd-700);
  }
`;

const LinkOverlayMobileContainer = styled(LinkOverlay)`
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  text-align: left;
`;

const PromoTitle = () => {
  return (
    <Horizontal gap={1} center>
      <Img src={icon} height="30" width="30" />
      <BrandS>
        <FormattedMessage
          id="Season2026.landing.promoBanner.title.welcomeOffer"
          defaultMessage="Welcome Offer"
        />
      </BrandS>
    </Horizontal>
  );
};

const PromoDescription = ({
  discountAmount,
}: {
  discountAmount: ReactNode;
}) => {
  return (
    <Description className="text-center">
      <FormattedMessage
        id="Season2026.landing.promoBanner.description"
        defaultMessage="Get {amount} free on your first cards!*"
        values={{
          amount: discountAmount,
        }}
      />
    </Description>
  );
};

const PromoDetails = ({
  discountAmount,
  discountPercentage,
  termsAndConditionsUrl,
}: {
  discountPercentage: number;
  discountAmount: ReactNode;
  termsAndConditionsUrl: string;
}) => {
  return (
    <BodyS>
      <FormattedMessage
        id="Season2026.landing.promoBanner.details"
        defaultMessage="*{percent} offered in credits, up to {amount} off. {link}"
        values={{
          amount: discountAmount,
          percent: (
            <FormattedNumber value={discountPercentage} style="percent" />
          ),
          link: (
            <LinkOther as={Link} to={termsAndConditionsUrl}>
              <UnderlinedText>
                <FormattedMessage
                  id="Season2026.landing.promoBanner.terms"
                  defaultMessage="Terms apply"
                />
              </UnderlinedText>
            </LinkOther>
          ),
        }}
      />
    </BodyS>
  );
};

const PromoButton = () => {
  const { signUp } = useConnectionContext();
  const track = useEvents();
  const leagueParam = useParams();
  const league = Object.values(leagueParam)[0];
  const onClick = () => {
    track('Click Landing Page Promo Banner', {
      clickSource: league,
    });
    signUp();
  };
  return (
    <LinkOverlay as={ButtonBase} onClick={onClick}>
      <Button asSpan color="secondary" size="small">
        <FormattedMessage id="promoBanner.cta" defaultMessage="Claim now!" />
      </Button>
    </LinkOverlay>
  );
};

type Props = {
  league?: string;
};

const LANDING_PROMO_BANNER_QUERY = gql`
  query LandingPromoBannerQuery($sport: Sport!, $affiliate: Boolean!) {
    market {
      id
      onboardingConversionCredit(sport: $sport, affiliate: $affiliate) {
        id
        percentageDiscount
        maxDiscount {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
    }
  }
  ${monetaryAmountFragment}
` as TypedDocumentNode<
  LandingPromoBannerQuery,
  LandingPromoBannerQueryVariables
>;

const LandingPromoBannerComponent = ({
  onboardingConversionCredit,
  league,
}: {
  onboardingConversionCredit: NonNullable<
    LandingPromoBannerQuery['market']['onboardingConversionCredit']
  >;
  league: string | undefined;
}) => {
  const isLaptop = useIsLaptop();
  const { signUp } = useConnectionContext();
  const track = useEvents();

  const {
    flags: { useLandingPagePromoBanner = false },
  } = useFeatureFlags();
  const { main: discountAmount } = useAmountWithConversion({
    monetaryAmount: onboardingConversionCredit.maxDiscount,
    hideFractionDigits: true,
  });

  const onClick = () => {
    track('Click Landing Page Promo Banner', {
      clickSource: league,
    });
    signUp();
  };

  if (!useLandingPagePromoBanner) {
    return null;
  }
  return (
    <LinkBoxWrapper className="dark-mode">
      <Content>
        {isLaptop ? (
          <Centered left={<PromoTitle />} right={<PromoButton />}>
            <Vertical gap={0} center>
              <PromoDescription discountAmount={discountAmount} />
              <PromoDetails
                discountAmount={discountAmount}
                discountPercentage={
                  onboardingConversionCredit.percentageDiscount
                }
                termsAndConditionsUrl={CREDIT_TERMS}
              />
            </Vertical>
          </Centered>
        ) : (
          <LinkOverlayMobileContainer as={ButtonBase} onClick={onClick}>
            <Horizontal gap={0}>
              <Img src={icon} height="45" width="45" />
              <PromoDescription discountAmount={discountAmount} />
            </Horizontal>

            <PromoDetails
              discountAmount={discountAmount}
              discountPercentage={onboardingConversionCredit.percentageDiscount}
              termsAndConditionsUrl={CREDIT_TERMS}
            />
          </LinkOverlayMobileContainer>
        )}
      </Content>
    </LinkBoxWrapper>
  );
};

export const LandingPromoBanner = ({ league }: Props) => {
  const { sport } = useSportContext();

  const [utmCampaign] = useLocalStorage(STORAGE.utmCampaign, undefined);
  const { data } = useQuery(LANDING_PROMO_BANNER_QUERY, {
    variables: {
      sport,
      affiliate: !!utmCampaign,
    },
  });
  const onboardingConversionCredit = data?.market.onboardingConversionCredit;

  if (!onboardingConversionCredit) {
    return null;
  }
  return (
    <LandingPromoBannerComponent
      onboardingConversionCredit={onboardingConversionCredit}
      league={league}
    />
  );
};
